import { EarningsPeriod } from '@api/api/earnings';
import { Entity } from '@api/api/entity-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { RevampTable, TableCell, TableHeader } from '@design/components/table';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import clsx from 'clsx';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpandableCell } from './ExpandableCell';
import styles from './TopCompetitors.module.scss';
import { useTopCompetitorColumns } from './useTopCompetitorColumns';
import { useTopCompetitorsData } from './useTopCompetitorsData';
import { TOP_COMPETITORS_LIMIT } from './utils';

export interface TopCompetitorsProps {
  entity: Entity;
  activeReportingPeriod: EarningsPeriod;
}

export const TopCompetitors = ({
  entity,
  activeReportingPeriod,
}: TopCompetitorsProps) => {
  const { t } = useTranslation(['earnings']);
  const [expandedTag, setExpandedTag] = useState<string | null>();
  const columns = useTopCompetitorColumns();
  const columnCount = columns.length + 1;
  const { openDrawer } = useEarningsInsightDrawer();
  const {
    trackPeerComparisonRowClicked,
    trackPeerComparisonReadEarningsInsightClicked,
  } = usePosthogEvents();

  const { rows, isLoading, error } = useTopCompetitorsData({
    entityTag: entity.tag,
    activeReportingPeriod,
  });

  if (error) {
    throw new Error('Failed to load TopCompetitors');
  }

  const firstExpandableTag = rows.find(row => row.earnings.hasReported)?.entity
    .tag;

  const activeExpandedTag =
    expandedTag === undefined ? firstExpandableTag : expandedTag;

  const onExpand = (tag: string) => {
    setExpandedTag(activeExpandedTag === tag ? null : tag);
  };

  const renderLoadingRows = () =>
    Array.from({ length: TOP_COMPETITORS_LIMIT }).map((_, index) => (
      <tr key={index}>
        <TableCell
          className={styles.cell}
          colspan={columnCount}
          isNumber={false}
        >
          <SkeletonLoader
            areas={[
              {
                styles: { width: '100%', height: '25px', borderRadius: '8px' },
              },
            ]}
          />
        </TableCell>
      </tr>
    ));

  return (
    <section className={styles.root} data-testid="top-competitors">
      <h3 className={styles.title}>
        {t('earnings:peerComparison.topCompetitors.title', {
          count: TOP_COMPETITORS_LIMIT,
        })}
      </h3>
      <div className={styles.tableWrapper}>
        <RevampTable withBodyRowHover={false} withBodyRowCursor={false}>
          <thead>
            <tr>
              <TableHeader
                className={clsx(styles.header, styles.expandColumn)}
                isHoverEnabled={false}
              />
              {columns.map(column => (
                <TableHeader
                  key={column.key}
                  className={clsx(styles.header, column.className)}
                  alignment={column.alignment}
                  isHoverEnabled={false}
                >
                  {column.label}
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              renderLoadingRows()
            ) : rows.length === 0 ? (
              <tr>
                <TableCell
                  className={clsx(styles.cell, styles.placeholder)}
                  colspan={columnCount}
                  alignment="start"
                  isNumber={false}
                >
                  {t('earnings:noDataToDisplay')}
                </TableCell>
              </tr>
            ) : (
              rows.map(row => {
                const {
                  entity: competitor,
                  earnings,
                  insightSummary,
                  earningsId,
                  hasEarningsInsight,
                } = row;
                const { tag } = competitor;
                const name = competitor.name_short || competitor.name;
                const canExpand = !!earnings.hasReported;

                const onExpandRow = () => {
                  if (!canExpand) {
                    return;
                  }

                  trackPeerComparisonRowClicked(
                    POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
                    tag,
                    earnings.reportingPeriod
                  );
                  onExpand(tag);
                };

                const onReadInsightClick = (
                  event: MouseEvent<HTMLButtonElement>
                ) => {
                  event.stopPropagation();
                  trackPeerComparisonReadEarningsInsightClicked(
                    POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
                    tag,
                    earnings.reportingPeriod
                  );

                  if (!earningsId) {
                    return;
                  }

                  openDrawer({
                    entityTag: tag,
                    earningsId,
                    subtab: earnings.hasReported
                      ? SubTabsType.Recap
                      : SubTabsType.Preview,
                    source: POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW,
                  });
                };

                return (
                  <ExpandableCell
                    key={tag}
                    name={name}
                    canExpand={canExpand}
                    isExpanded={canExpand && activeExpandedTag === tag}
                    insightSummary={insightSummary}
                    hasEarningsInsight={hasEarningsInsight}
                    columnCount={columnCount}
                    onExpand={onExpandRow}
                    onReadInsightClick={onReadInsightClick}
                  >
                    {columns.map(column => (
                      <TableCell
                        key={column.key}
                        className={clsx(styles.cell, column.className)}
                        alignment={column.alignment}
                        isNumber={false}
                      >
                        {column.cell(row)}
                      </TableCell>
                    ))}
                  </ExpandableCell>
                );
              })
            )}
          </tbody>
        </RevampTable>
      </div>
    </section>
  );
};
